/**
 * Featured placement add-on. Stripe collection is still on hold; TEST uses
 * a listing flag so the homepage can show paid-placement examples.
 */
import { listingCanRenderOnListingPage } from './listingProcess';
export const FEATURED_PRICE_USD = 10;
export const FEATURED_PRICE_YEAR_USD = 100;
export const FEATURED_PRICE_SUBUNITS = FEATURED_PRICE_USD * 100;
export const FEATURED_PRICE_YEAR_SUBUNITS = FEATURED_PRICE_YEAR_USD * 100;
export const FEATURED_CURRENCY = 'USD';
export const FEATURED_PERIOD = 'month';

/**
 * Intl values for Featured copy: $10/month or $100/year.
 *
 * @returns {{ monthly: number, yearly: number, price: number }}
 */
export const featuredPriceValues = () => ({
  monthly: FEATURED_PRICE_USD,
  yearly: FEATURED_PRICE_YEAR_USD,
  price: FEATURED_PRICE_USD,
});

/**
 * Sharetribe listing id as a UUID string.
 *
 * @param {Object|string|null} listing
 * @returns {string|null}
 */
export const listingIdOf = listing => {
  if (!listing) {
    return null;
  }
  if (typeof listing === 'string') {
    return listing;
  }
  if (typeof listing.id === 'string') {
    return listing.id;
  }
  return listing.id?.uuid || listing.uuid || null;
};

export const isListingFeatured = listing => {
  const publicData = listing?.attributes?.publicData || {};
  if (!publicData.featured) {
    return false;
  }
  if (!publicData.featuredUntil) {
    return true;
  }
  const until = Date.parse(publicData.featuredUntil);
  return Number.isFinite(until) && until > Date.now();
};

/**
 * Homepage strip: published, not deleted, and paid/active Featured.
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const isPublishedFeaturedListing = listing =>
  listing?.attributes?.state === 'published' &&
  !listing?.attributes?.deleted &&
  isListingFeatured(listing);

/**
 * Any published listener listing the homepage may show. Featured is a
 * sort hint, not an eligibility filter.
 *
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const isPublishedListenerListing = listing => {
  if (listing?.attributes?.state !== 'published' || listing?.attributes?.deleted) {
    return false;
  }
  const listingType = listing?.attributes?.publicData?.listingType;
  return !listingType || listingType === 'talksession';
};

export const FEATURED_STRIP_LIMIT = 4;
export const HOMEPAGE_LISTENER_PAGE_SIZE = 50;

/**
 * True only when this listings.query page is the full published set.
 * A short page, a missing total, or a 429 must not count as complete.
 *
 * @param {Object} [params]
 * @param {Array} [params.listings]
 * @param {Object} [params.meta]
 * @param {number} [params.perPage]
 * @returns {boolean}
 */
export const homepageQueryIsComplete = ({ listings = [], meta = {}, perPage } = {}) => {
  const returned = Array.isArray(listings) ? listings.length : 0;
  const totalItems = Number(meta?.totalItems);
  if (Number.isFinite(totalItems)) {
    return returned >= totalItems;
  }
  const pageSize = Number(perPage);
  if (Number.isFinite(pageSize) && pageSize > 0) {
    // A full page without a total is possibly truncated. Empty is complete
    // only when the API said totalItems is 0 (handled above).
    return returned > 0 && returned < pageSize;
  }
  return false;
};

/**
 * Homepage should keep the last complete Featured strip when a later
 * Sharetribe query is 429’d, empty, or shorter. Never replace 3–4 cards
 * with a silent one-card list.
 *
 * @param {Array} [previous]
 * @param {Array} [next]
 * @returns {Array}
 */
export const preferCompleteFeaturedRefs = (previous = [], next = []) => {
  const prev = Array.isArray(previous) ? previous : [];
  const incoming = Array.isArray(next) ? next : [];
  if (incoming.length === 0) {
    return prev.length ? prev : incoming;
  }
  if (prev.length > incoming.length) {
    return prev;
  }
  return incoming;
};

/**
 * Decide which listing refs the homepage may cache as the published set.
 * A partial or 429 result is never stored as complete.
 *
 * @param {Object} [params]
 * @param {Array} [params.previous]
 * @param {boolean} [params.previousComplete]
 * @param {Array} [params.incoming]
 * @param {boolean} [params.incomingComplete]
 * @returns {{ listingRefs: Array, complete: boolean }}
 */
export const resolveHomepageListingRefs = ({
  previous = [],
  previousComplete = false,
  incoming = [],
  incomingComplete = false,
} = {}) => {
  const prev = Array.isArray(previous) ? previous : [];
  const next = Array.isArray(incoming) ? incoming : [];

  if (next.length === 0) {
    return {
      listingRefs: prev,
      complete: previousComplete && prev.length > 0,
    };
  }

  if (incomingComplete) {
    return { listingRefs: next, complete: true };
  }

  if (previousComplete && prev.length > 0) {
    return { listingRefs: prev, complete: true };
  }

  return { listingRefs: next, complete: false };
};

/**
 * Public listings.query can lag just after Get featured. Overlay the owner’s
 * ownListings featured flags so a just-toggled-off profile is dropped. Own
 * listings are not appended — they stay off Featured / discovery.
 *
 * @param {Array<Object>} [publicListings]
 * @param {Array<Object>} [ownListings]
 * @returns {Array<Object>}
 */
export const listingsWithOwnFeaturedFlags = (publicListings = [], ownListings = []) => {
  const ownById = new Map();
  (ownListings || []).forEach(listing => {
    const id = listingIdOf(listing);
    if (id) {
      ownById.set(id, listing);
    }
  });

  const seen = new Set();
  const merged = (publicListings || []).map(listing => {
    const id = listingIdOf(listing);
    if (id) {
      seen.add(id);
    }
    const own = ownById.get(id);
    if (!own) {
      return listing;
    }
    return {
      ...listing,
      attributes: {
        ...listing.attributes,
        publicData: {
          ...(listing.attributes?.publicData || {}),
          featured: own.attributes?.publicData?.featured,
          featuredUntil: own.attributes?.publicData?.featuredUntil,
        },
      },
    };
  });

  // Own listings stay off Featured / discovery. Overlay flags only so a
  // just-toggled-off Featured card is dropped from the public strip.
  return merged;
};

/**
 * Homepage Featured strip: seeded examples, then other published Featured.
 * The current user’s own listing never appears here (Dashboard / Profile /
 * Edit still reach it). Non-Featured and expired flags never appear.
 *
 * @param {Object} [params]
 * @param {Array<Object>} [params.own]
 * @param {Array<Object>} [params.others]
 * @param {Array<string>} [params.preferredIds]
 * @param {number} [params.limit]
 * @returns {Array<Object>}
 */
export const mergeHomepageFeaturedListings = ({
  own = [],
  others = [],
  preferredIds = [],
  limit = FEATURED_STRIP_LIMIT,
} = {}) => {
  const overlaid = listingsWithOwnFeaturedFlags(others, own);
  const ownIds = new Set((own || []).map(listingIdOf).filter(Boolean));
  const seen = new Set();
  const merged = [];

  const push = listing => {
    const id = listingIdOf(listing);
    if (!id || seen.has(id) || ownIds.has(id) || !isPublishedFeaturedListing(listing)) {
      return;
    }
    seen.add(id);
    merged.push(listing);
  };

  const preferred = [];
  const rest = [];
  overlaid.forEach(listing => {
    const id = listingIdOf(listing);
    if (ownIds.has(id)) {
      return;
    }
    if (preferredIds.includes(id)) {
      preferred.push(listing);
    } else {
      rest.push(listing);
    }
  });
  preferred
    .sort((a, b) => preferredIds.indexOf(listingIdOf(a)) - preferredIds.indexOf(listingIdOf(b)))
    .forEach(push);
  rest.forEach(push);

  return merged.slice(0, limit);
};

/**
 * Homepage cards: every published talksession listing, seeded / Featured
 * first. Own listings stay off the strip. No card-count cap.
 *
 * @param {Object} [params]
 * @param {Array<Object>} [params.own]
 * @param {Array<Object>} [params.others]
 * @param {Array<string>} [params.preferredIds]
 * @returns {Array<Object>}
 */
export const mergeHomepagePublishedListeners = ({
  own = [],
  others = [],
  preferredIds = [],
} = {}) => {
  const overlaid = listingsWithOwnFeaturedFlags(others, own);
  const ownIds = new Set((own || []).map(listingIdOf).filter(Boolean));
  const seen = new Set();
  const merged = [];

  const push = listing => {
    const id = listingIdOf(listing);
    if (
      !id ||
      seen.has(id) ||
      ownIds.has(id) ||
      !isPublishedListenerListing(listing) ||
      !listingCanRenderOnListingPage(listing)
    ) {
      return;
    }
    seen.add(id);
    merged.push(listing);
  };

  const preferred = [];
  const featured = [];
  const rest = [];
  overlaid.forEach(listing => {
    const id = listingIdOf(listing);
    if (!id || ownIds.has(id)) {
      return;
    }
    if (preferredIds.includes(id)) {
      preferred.push(listing);
    } else if (isListingFeatured(listing)) {
      featured.push(listing);
    } else {
      rest.push(listing);
    }
  });
  preferred
    .sort((a, b) => preferredIds.indexOf(listingIdOf(a)) - preferredIds.indexOf(listingIdOf(b)))
    .forEach(push);
  featured.forEach(push);
  rest.forEach(push);

  return merged;
};

export const featuredUntilLabel = listing => {
  const until = listing?.attributes?.publicData?.featuredUntil;
  if (!until) {
    return null;
  }
  const date = new Date(until);
  return Number.isNaN(date.getTime()) ? null : date;
};
